// src/pages/SettingsPage.js

import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import InviteUser from '../components/InviteUser';
import AssignTrainee from '../components/AssignTrainee';
import AssignmentHistory from '../components/AssignmentHistory';
import ArchiveTrainees from '../components/ArchiveTrainees';
import UserManagement from '../components/UserManagement';
import BroadcastNotification from '../components/BroadcastNotification';
import Modal from '../components/Modal';
import { Link } from 'react-router-dom';
import { FiSettings, FiActivity, FiUser, FiClock, FiInfo, FiChevronDown, FiLogIn, FiLogOut, FiAlertTriangle, FiUserPlus, FiEdit, FiTrash, FiBookOpen, FiDownload, FiDatabase, FiFileText } from 'react-icons/fi';
import { db } from '../utils/firebaseConfig';
import { collection, query, orderBy, limit, getDocs, startAfter, doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { pdfStyles } from '../utils/pdfStyles';

pdfMake.vfs = pdfFonts.vfs;

// Utility function to get image for PDF
const getBase64ImageFromURL = async (url) => {
  const full = url.startsWith('/') ? `${process.env.PUBLIC_URL || ''}${url}` : url;
  const res  = await fetch(`${full}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Image fetch failed');
  const blob  = await res.blob();
  return new Promise((r, j) => { const f=new FileReader(); f.onloadend=()=>r(f.result); f.onerror=j; f.readAsDataURL(blob); });
};

const LoadingSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    {/* Header Skeleton */}
    <div className="flex items-center justify-between mb-6">
      <div className="h-8 w-48 bg-secondary-light dark:bg-secondary-dark rounded"></div>
    </div>

    {/* Settings Sections Skeleton */}
    <div className="space-y-6">
      {[1, 2, 3].map((section) => (
        <div 
          key={section}
          className="bg-primary-light dark:bg-primary-dark p-6 rounded-xl shadow-lg border border-secondary-light dark:border-secondary-dark"
        >
          <div className="h-6 w-40 bg-secondary-light dark:bg-secondary-dark rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2].map((item) => (
              <div key={item} className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 w-48 bg-secondary-light dark:bg-secondary-dark rounded"></div>
                  <div className="h-3 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
                </div>
                <div className="h-8 w-16 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SettingsPage = () => {
  const { role } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedTraineeId, setSelectedTraineeId] = useState(null);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isTraineeAssignmentsOpen, setIsTraineeAssignmentsOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userNameCache, setUserNameCache] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const scrollPositionRef = useRef(0);
  const LOGS_PER_PAGE = 10;

  const formatAction = (action) => {
    if (!action) return 'Unknown Action';
    
    // Replace underscores with spaces and convert to title case
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // --- Map actions to icons and colors --- 
  const getActionStyle = (action) => {
    // Default style
    let style = { icon: FiInfo, colorClass: 'text-gray-600 dark:text-gray-400', borderClass: 'border-l-gray-500' };

    // Map specific actions
    if (action?.includes('LOGIN_SUCCESS')) {
      style = { icon: FiLogIn, colorClass: 'text-green-600 dark:text-green-400', borderClass: 'border-l-green-500' };
    } else if (action?.includes('LOGOUT')) {
      style = { icon: FiLogOut, colorClass: 'text-blue-600 dark:text-blue-400', borderClass: 'border-l-blue-500' };
    } else if (action?.includes('FAILED') || action?.includes('ERROR')) {
      style = { icon: FiAlertTriangle, colorClass: 'text-red-600 dark:text-red-400', borderClass: 'border-l-red-500' };
    } else if (action?.includes('INVITED') || action?.includes('ADDED')) {
      style = { icon: FiUserPlus, colorClass: 'text-purple-600 dark:text-purple-400', borderClass: 'border-l-purple-500' };
    } else if (action?.includes('ASSIGNED') || action?.includes('UPDATED') || action?.includes('EDITED')) {
      style = { icon: FiEdit, colorClass: 'text-yellow-600 dark:text-yellow-400', borderClass: 'border-l-yellow-500' };
    } else if (action?.includes('DELETED') || action?.includes('ARCHIVED') || action?.includes('REMOVED')) {
      style = { icon: FiTrash, colorClass: 'text-orange-600 dark:text-orange-400', borderClass: 'border-l-orange-500' };
    } // Add more specific cases as needed

    return style;
  };

  // Helper function to fetch user name from Firestore
  const fetchUserName = useCallback(async (userId) => {
    if (!userId) return null;
    if (userNameCache[userId]) {
      return userNameCache[userId];
    }
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const name = userData.name || userData.displayName || 'Unknown User';
        setUserNameCache(prev => ({ ...prev, [userId]: name }));
        return name;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    return null;
  }, [userNameCache]);

  const fetchAuditLogs = useCallback(async (loadMore = false) => {
    if (loadMore) {
      scrollPositionRef.current = window.scrollY;
      setLoadingMore(true);
    } else {
       setLoading(true);
    }

    try {
      let auditQuery;

      if (loadMore && lastDoc) {
        auditQuery = query(
          collection(db, 'audit_logs'),
          orderBy('timestamp', 'desc'),
          startAfter(lastDoc),
          limit(LOGS_PER_PAGE)
        );
      } else {
        auditQuery = query(
          collection(db, 'audit_logs'),
          orderBy('timestamp', 'desc'),
          limit(LOGS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(auditQuery);
      
      if (querySnapshot.empty) {
        if (loadMore) {
          setHasMore(false);
        } else {
          setAuditLogs([]);
          setHasMore(false);
        }
        return;
      }

      const logs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        };
      });

      const processedLogs = await Promise.all(logs.map(async (log) => {
        let enhancedLog = { ...log };
        if ((!enhancedLog.userName || enhancedLog.userName === 'unknown' || enhancedLog.userName === 'Unknown User') && enhancedLog.userId) {
          const userNameFromDb = await fetchUserName(enhancedLog.userId);
          if (userNameFromDb) {
            enhancedLog.userName = userNameFromDb;
          }
        }
        if ((log.action === 'ASSIGN_TRAINEE' || log.action === 'UNASSIGN_TRAINEE') && (!log.userName || log.userName === 'unknown')) {
          if (log.details && log.details.trainerId) {
            const trainerName = await fetchUserName(log.details.trainerId);
            if (trainerName) {
              enhancedLog.details = { ...enhancedLog.details, 'Trainer Name': trainerName };
              if (!log.userName || log.userName === 'unknown') {
                enhancedLog.userName = trainerName;
              }
            }
            if (log.details.traineeId) {
              const traineeName = await fetchUserName(log.details.traineeId);
              if (traineeName) {
                enhancedLog.details = { ...enhancedLog.details, 'Trainee Name': traineeName };
              }
            }
          }
        }
        if (log.details && log.details.targetUserId && !log.targetUserName) {
          const userName = await fetchUserName(log.details.targetUserId);
          if (userName) {
            enhancedLog.targetUserName = userName;
          }
        }
        if (log.details) {
          const specialFields = ['trainerId', 'Trainer Id', 'traineeId', 'Trainee Id'];
          for (const field of specialFields) {
            if (log.details[field] && typeof log.details[field] === 'string') {
              const userId = log.details[field];
              const isFirebaseId = userId.length > 15 && !userId.includes(' ');
              if (isFirebaseId) {
                const userName = await fetchUserName(userId);
                if (userName) {
                  const displayField = field.replace('Id', 'Name').replace(' Id', ' Name');
                  enhancedLog.details = {
                    ...enhancedLog.details,
                    [displayField]: userName,
                  };
                  enhancedLog._userNames = {
                    ...(enhancedLog._userNames || {}),
                    [userId]: userName,
                  };
                }
              }
            }
          }
        }
        return enhancedLog;
      }));

      setHasMore(logs.length === LOGS_PER_PAGE);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);

      if (loadMore) {
        setAuditLogs(prev => [...prev, ...processedLogs]);
      } else {
        setAuditLogs(processedLogs);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs.');
    } finally {
      if (loadMore) setLoadingMore(false);
      else setLoading(false);
    }
  }, [lastDoc, LOGS_PER_PAGE, fetchUserName]);

  useEffect(() => {
    if (role === 'admin') {
      fetchAuditLogs();
    }
  }, [role, fetchAuditLogs]);

  useEffect(() => {
    if (!loadingMore && scrollPositionRef.current > 0) {
       requestAnimationFrame(() => {
          window.scrollTo(0, scrollPositionRef.current);
          scrollPositionRef.current = 0; 
       });
    }
  }, [auditLogs, loadingMore]);

  // Generate PDF for audit logs - updated to fetch all logs
  const generateAuditLogsPdf = async () => {
    if (generatingPdf) return;
    
    setGeneratingPdf(true);
    try {
      // Fetch all audit logs for the PDF
      const allLogs = [];
      let lastLogDoc = null;
      let hasMoreLogs = true;
      const FETCH_LIMIT = 50; // Fetch in larger batches for efficiency
      
      toast.info('Preparing PDF data...');
      
      // Fetch all logs in batches
      while (hasMoreLogs) {
        let logsQuery;
        
        if (lastLogDoc) {
          logsQuery = query(
            collection(db, 'audit_logs'),
            orderBy('timestamp', 'desc'),
            startAfter(lastLogDoc),
            limit(FETCH_LIMIT)
          );
        } else {
          logsQuery = query(
            collection(db, 'audit_logs'),
            orderBy('timestamp', 'desc'),
            limit(FETCH_LIMIT)
          );
        }
        
        const querySnapshot = await getDocs(logsQuery);
        
        if (querySnapshot.empty) {
          hasMoreLogs = false;
          break;
        }
        
        const fetchedLogs = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date()
          };
        });
        
        // Process user names for the logs
        const processedLogs = await Promise.all(fetchedLogs.map(async (log) => {
          let enhancedLog = { ...log };
          
          // Fix unknown usernames
          if ((!enhancedLog.userName || enhancedLog.userName === 'unknown' || enhancedLog.userName === 'Unknown User') && enhancedLog.userId) {
            const userName = await fetchUserName(enhancedLog.userId);
            if (userName) {
              enhancedLog.userName = userName;
            }
          }
          
          // For ASSIGN/UNASSIGN actions, make sure the person doing the assigning is identified
          if ((log.action === 'ASSIGN_TRAINEE' || log.action === 'UNASSIGN_TRAINEE') && (!log.userName || log.userName === 'unknown')) {
            // Try to get username from details
            if (log.details && log.details.trainerId) {
              // First try to fetch the trainer name directly
              const trainerName = await fetchUserName(log.details.trainerId);
              if (trainerName) {
                // Store the trainer name for reference
                enhancedLog.details = {
                  ...enhancedLog.details,
                  'Trainer Name': trainerName
                };
                
                // If we don't have a userName for the action, use the trainer
                if (!log.userName || log.userName === 'unknown') {
                  enhancedLog.userName = trainerName;
                }
              }
              
              // Try to fetch the trainee name
              if (log.details.traineeId) {
                const traineeName = await fetchUserName(log.details.traineeId);
                if (traineeName) {
                  enhancedLog.details = {
                    ...enhancedLog.details,
                    'Trainee Name': traineeName
                  };
                }
              }
            }
          }
          
          // Get target user name if available
          if (log.details && log.details.targetUserId && !log.targetUserName) {
            const userName = await fetchUserName(log.details.targetUserId);
            if (userName) {
              enhancedLog.targetUserName = userName;
            }
          }

          // Resolve trainer/trainee IDs for assignment actions
          if (log.details) {
            const specialFields = ['trainerId', 'Trainer Id', 'traineeId', 'Trainee Id'];
            for (const field of specialFields) {
              if (log.details[field] && typeof log.details[field] === 'string') {
                const userId = log.details[field];
                const isFirebaseId = userId.length > 15 && !userId.includes(' ');
                if (isFirebaseId) {
                  const userName = await fetchUserName(userId);
                  if (userName) {
                    const displayField = field.replace('Id', 'Name').replace(' Id', ' Name');
                    enhancedLog.details = {
                      ...enhancedLog.details,
                      [displayField]: userName,
                    };
                    enhancedLog._userNames = {
                      ...(enhancedLog._userNames || {}),
                      [userId]: userName,
                    };
                  }
                }
              }
            }
          }

          return enhancedLog;
        }));
        
        allLogs.push(...processedLogs);
        
        if (querySnapshot.docs.length < FETCH_LIMIT) {
          hasMoreLogs = false;
        } else {
          lastLogDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        }
      }
      
      toast.info(`Generating PDF with ${allLogs.length} audit entries...`);
      
      // Create PDF document
      const content = [];
      
      // PDF Colors matching DOR style
      const PDF_COLORS = {
        primary: '#0A4DA2',
        secondary: '#3b82f6',
        accent: '#93c5fd',
        success: '#16a34a',
        warning: '#d97706',
        danger: '#dc2626',
        tableHeader: '#E2E8F0',
        tableAlt: '#f8fafc',
        text: '#1e293b',
        lightText: '#64748b',
        border: '#cbd5e1',
        headerBg: '#dbeafe',
      };
      
      // Add banner image if available
      try {
        const bannerImage = await getBase64ImageFromURL('/banner.png');
        content.push({ 
          image: bannerImage, 
          width: 515, 
          alignment: 'center', 
          margin: [0, 0, 0, 10] 
        });
      } catch (error) {
        content.push({
          text: 'FIELD TRAINING PROGRAM',
          fontSize: 26,
          bold: true,
          color: PDF_COLORS.primary,
          alignment: 'center',
          margin: [0, 0, 0, 10]
        });
      }
      
      // Add title and timestamp with enhanced styling
      content.push(
        { 
          text: 'System Audit Log Report', 
          fontSize: 24,
          bold: true,
          color: PDF_COLORS.primary,
          alignment: 'center',
          margin: [0, 0, 0, 8]
        },
        { 
          canvas: [
            {
              type: 'line',
              x1: 0, y1: 0,
              x2: 515, y2: 0,
              lineWidth: 2,
              lineColor: PDF_COLORS.secondary,
            }
          ],
          margin: [0, 0, 0, 15]
        }
      );
      
      // Info box with report details
      content.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                table: {
                  widths: ['33%', '33%', '34%'],
                  body: [
                    [
                      { 
                        text: `Report Date: ${format(new Date(), 'MMMM d, yyyy')}`,
                        fontSize: 10,
                        color: PDF_COLORS.text,
                        fillColor: PDF_COLORS.headerBg,
                        margin: [8, 8],
                        border: [true, true, false, true],
                        borderColor: [PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border],
                      },
                      { 
                        text: `Time: ${format(new Date(), 'h:mm a')}`,
                        fontSize: 10,
                        color: PDF_COLORS.text,
                        fillColor: PDF_COLORS.headerBg,
                        margin: [8, 8],
                        border: [false, true, false, true],
                        borderColor: [PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border],
                      },
                      { 
                        text: `Total Entries: ${allLogs.length.toLocaleString()}`,
                        fontSize: 10,
                        color: PDF_COLORS.text,
                        fillColor: PDF_COLORS.headerBg,
                        margin: [8, 8],
                        border: [false, true, true, true],
                        borderColor: [PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border],
                      }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => PDF_COLORS.border,
                  vLineColor: () => PDF_COLORS.border,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                }
              }
            ]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20],
      });
      
      // Group logs by date for better organization
      const groupedByDate = {};
      allLogs.forEach(log => {
        const dateKey = format(log.timestamp, 'MMMM d, yyyy');
        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = [];
        }
        groupedByDate[dateKey].push(log);
      });
      
      // Create a table for each date
      Object.entries(groupedByDate).forEach(([dateKey, logs], dateIndex) => {
        // Add date header with enhanced styling
        content.push({
          text: dateKey,
          fontSize: 14,
          bold: true,
          color: PDF_COLORS.primary,
          margin: [0, dateIndex > 0 ? 20 : 0, 0, 10],
          pageBreak: dateIndex > 0 && logs.length > 20 ? 'before' : undefined
        });
        
        // Create the table for this date
        const tableBody = [];
        
        // Table header
        tableBody.push([
          { text: 'Time', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, color: PDF_COLORS.text, bold: true },
          { text: 'User', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, color: PDF_COLORS.text, bold: true },
          { text: 'Action', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, color: PDF_COLORS.text, bold: true },
          { text: 'Details', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, color: PDF_COLORS.text, bold: true }
        ]);
        
        // Table rows
        logs.forEach(log => {
          const action = formatAction(log.action);
          
          // Get color for the action
          const { colorClass } = getActionStyle(log.action);
          const colorMapping = {
            'text-green-600': '#22c55e',
            'text-blue-600': '#2563eb',
            'text-red-600': '#dc2626',
            'text-purple-600': '#9333ea',
            'text-yellow-600': '#ca8a04',
            'text-orange-600': '#ea580c',
            'text-gray-600': '#4b5563'
          };
          const color = colorMapping[colorClass.split(' ')[0]] || '#4b5563';
          
          // Format details for display
          let detailsText = '';
          if (log.details && Object.keys(log.details).length > 0) {
            const relevantDetails = Object.entries(log.details)
              .filter(([key]) => key !== 'traineeId')
              .map(([key, value]) => {
                const formattedKey = key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, str => str.toUpperCase())
                  .trim();
                
                const formattedValue = formatValue(key, value, log);
                return `${formattedKey}: ${formattedValue}`;
              })
              .join('\n');
            
            detailsText = relevantDetails;
          }
          
          // Add the row to the table
          const rowIndex = tableBody.length - 1;
          tableBody.push([
            { 
              text: format(log.timestamp, 'h:mm a'),
              fontSize: 9,
              color: PDF_COLORS.text,
              fillColor: rowIndex % 2 === 0 ? 'white' : PDF_COLORS.tableAlt,
              margin: [5, 5, 5, 5]
            },
            { 
              text: log.userName || 'Unknown User',
              fontSize: 9,
              color: PDF_COLORS.text,
              fillColor: rowIndex % 2 === 0 ? 'white' : PDF_COLORS.tableAlt,
              margin: [5, 5, 5, 5]
            },
            { 
              text: action + (log.targetUserName ? ` - ${log.targetUserName}` : ''),
              fontSize: 9,
              bold: true,
              color: color,
              fillColor: rowIndex % 2 === 0 ? 'white' : PDF_COLORS.tableAlt,
              margin: [5, 5, 5, 5]
            },
            { 
              text: detailsText,
              fontSize: 8,
              color: PDF_COLORS.text,
              fillColor: rowIndex % 2 === 0 ? 'white' : PDF_COLORS.tableAlt,
              margin: [5, 5, 5, 5]
            }
          ]);
        });
        
        // Add the table to the content
        content.push({
          table: {
            headerRows: 1,
            widths: [70, 120, '*', 180],
            body: tableBody
          },
          layout: {
            hLineWidth: function(i, node) {
              return i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5;
            },
            vLineWidth: function() {
              return 0;
            },
            hLineColor: function() {
              return PDF_COLORS.border;
            },
            paddingLeft: function() {
              return 0;
            },
            paddingRight: function() {
              return 0;
            },
            paddingTop: function() {
              return 0;
            },
            paddingBottom: function() {
              return 0;
            }
          }
        });
      });
      
      // Add audit compliance footer section
      content.push(
        { text: '', margin: [0, 40, 0, 0] },
        {
          text: 'Audit Compliance Certification',
          fontSize: 14,
          bold: true,
          color: PDF_COLORS.primary,
          margin: [0, 0, 0, 15]
        },
        {
          text: 'This system audit log report is generated in compliance with departmental record-keeping policies and procedures. All entries are automatically captured and timestamped by the Field Training Management System.',
          fontSize: 9,
          color: PDF_COLORS.lightText,
          alignment: 'justify',
          margin: [0, 0, 0, 20]
        },
        {
          columns: [
            {
              width: '50%',
              stack: [
                {
                  canvas: [{
                    type: 'line',
                    x1: 0, y1: 0,
                    x2: 200, y2: 0,
                    lineWidth: 1,
                    lineColor: PDF_COLORS.text,
                    dash: { length: 1 },
                  }],
                  margin: [0, 20, 0, 5],
                },
                { text: 'Reviewing Officer Signature', fontSize: 9, color: PDF_COLORS.lightText },
                { text: 'Date: _____________________', fontSize: 9, color: PDF_COLORS.lightText, margin: [0, 10, 0, 0] },
              ]
            },
            {
              width: '50%',
              stack: [
                {
                  canvas: [{
                    type: 'line',
                    x1: 0, y1: 0,
                    x2: 200, y2: 0,
                    lineWidth: 1,
                    lineColor: PDF_COLORS.text,
                    dash: { length: 1 },
                  }],
                  margin: [0, 20, 0, 5],
                  alignment: 'right',
                },
                { text: 'Command Staff Signature', fontSize: 9, color: PDF_COLORS.lightText, alignment: 'right' },
                { text: 'Date: _____________________', fontSize: 9, color: PDF_COLORS.lightText, alignment: 'right', margin: [0, 10, 0, 0] },
              ]
            }
          ]
        }
      );
      
      // Custom PDF styles that extend the shared pdfStyles
      const auditLogStyles = {
        ...pdfStyles,
        dateHeader: {
          fontSize: 14,
          bold: true,
          color: '#111827',
          margin: [0, 10, 0, 5]
        },
        tableHeader: {
          fontSize: 10,
          bold: true,
          color: '#374151'
        },
        tableCell: {
          fontSize: 9,
          color: '#1f2937'
        },
        tableCellAction: {
          fontSize: 9,
          bold: true
        }
      };
      
      // Generate and download the PDF
      pdfMake.createPdf({ 
        pageMargins: [40, 20, 40, 40],
        footer: function(currentPage, pageCount) {
          return {
            columns: [
              {
                text: 'System Audit Log - Confidential',
                fontSize: 8,
                color: '#6B7280',
                margin: [40, 10, 0, 0]
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                alignment: 'right',
                fontSize: 8,
                color: '#6B7280',
                margin: [0, 10, 40, 0]
              }
            ]
          };
        },
        content, 
        styles: {
          ...auditLogStyles,
          tableHeader: {
            ...pdfStyles.tableHeader,
            fontSize: 9,
            bold: true,
            color: PDF_COLORS.text
          },
          tableCell: {
            ...pdfStyles.tableCell,
            fontSize: 9,
            color: PDF_COLORS.text
          }
        }
      }).download(`System_Audit_Log_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      
      toast.success(`Audit log PDF generated successfully! ${allLogs.length.toLocaleString()} entries included.`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Helper function to format values for both display and PDF
  const formatValue = (key, value, log) => {
    // Skip null/undefined values
    if (value == null) return '';
    
    // Special case: For some actions like "Deactivate User", the targetUserId is already 
    // showing the user's name, not a Firebase ID
    if ((key === 'targetUserId' || key === 'Target User Id') && 
        typeof value === 'string' &&
        // Check if this looks like a human name (contains space but is shorter than 15 chars)
        (value.includes(' ') && value.length < 15)) {
      return value; // It's already a human-readable name, don't try to replace it
    }
    
    // Special case for trainer/trainee IDs
    if ((key === 'trainerId' || key === 'Trainer Id' || key === 'traineeId' || key === 'Trainee Id') &&
        typeof value === 'string' && 
        value.length > 15) {
      
      // First check if we have a matching Name field
      const nameField = key.replace('Id', 'Name').replace(' Id', ' Name');
      if (log.details && log.details[nameField]) {
        return log.details[nameField];
      }
      
      // Then check the _userNames cache
      if (log._userNames && log._userNames[value]) {
        return log._userNames[value];
      }
      
      // Fallback to looking up the ID
      return `Unknown ${key.includes('Trainer') ? 'Trainer' : 'Trainee'}`;
    }
    
    // Handle general User ID fields
    if ((key === 'targetUserId' || key === 'Target User Id' || 
        (key.toLowerCase().includes('user') && key.toLowerCase().includes('id'))) && 
        typeof value === 'string' && 
        value.length > 15) { // Firebase IDs are usually long strings
      
      // Check in multiple places for the user name
      
      // 1. Check if we have a targetUserName directly in the log
      if (log.targetUserName && key.toLowerCase().includes('target')) {
        return log.targetUserName;
      }
      
      // 2. Check if we have this user's name in the _userNames cache
      if (log._userNames && log._userNames[value]) {
        return log._userNames[value];
      }
      
      // 3. Check in the details object for possible names
      if (log.details) {
        const nameFields = ['userName', 'name', 'fullName', 'displayName'];
        for (const field of nameFields) {
          if (log.details[field]) {
            return log.details[field];
          }
        }
      }
      
      // If we still don't have a name, just show "User" rather than the ID
      return `Unknown User`;
    }
    
    // Return the original value for all other cases
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };

  const AuditLogEntry = ({ log }) => {
    const { icon: ActionIcon, colorClass, borderClass } = getActionStyle(log.action);

    // Find affected user name from log details
    const getAffectedUserName = () => {
      if (log.targetUserName) {
        return log.targetUserName;
      }
      
      if (!log.details) return null;
      
      // Get user from various possible fields
      if (log.action?.includes('ASSIGN') && log.details['Trainer Name']) {
        return log.details['Trainer Name']; // For assignment logs, show trainer name
      }
      
      if (log.details['Trainee Name']) {
        return log.details['Trainee Name'];
      }
      
      // Try different possible field names where user name might be stored
      const possibleNameFields = ['userName', 'fullName', 'name', 'displayName', 'userFullName'];
      for (const field of possibleNameFields) {
        if (log.details[field]) {
          return log.details[field];
        }
      }
      
      // Also check for IDs that might have names in the _userNames cache
      const idFields = ['trainerId', 'Trainer Id', 'traineeId', 'Trainee Id', 'targetUserId', 'Target User Id'];
      for (const field of idFields) {
        const id = log.details[field];
        if (id && log._userNames && log._userNames[id]) {
          return log._userNames[id];
        }
      }
      
      return null;
    };
    
    const affectedUserName = getAffectedUserName();

    // Group related details together for better display
    const groupedDetails = () => {
      if (!log.details) return [];
      
      const entries = Object.entries(log.details)
        .filter(([key]) => 
          // Show name fields only if we don't have the corresponding ID field also showing
          !(key.includes('Name') && log.details[key.replace('Name', 'Id')] && 
            formatValue(key.replace('Name', 'Id'), log.details[key.replace('Name', 'Id')], log) === log.details[key])
        );
      
      // Show most important fields first - trainer/trainee fields at the top
      const orderedKeys = ['Trainer Id', 'trainerId', 'Trainee Id', 'traineeId', 'requiredHours', 'Required Hours', 'userName', 'name', 'targetUserId', 'Target User Id'];
      
      return entries.sort(([keyA], [keyB]) => {
        const indexA = orderedKeys.indexOf(keyA);
        const indexB = orderedKeys.indexOf(keyB);
        
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
    };

    return (
      // Add left border and slightly more padding
      <div className={`p-4 rounded-lg bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark border-l-4 ${borderClass}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-2">
           {/* Timestamp and User */}
           <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              <FiClock className="mr-1.5 flex-shrink-0 w-3.5 h-3.5" />
              {format(log.timestamp, 'MMM d, yyyy h:mm a')}
           </div>
           <div className="flex items-center text-xs font-medium">
             <FiUser className="mr-1.5 flex-shrink-0 w-3.5 h-3.5" />
             <span className="truncate" title={log.userName || 'Unknown User'}>{log.userName || 'Unknown User'}</span>
           </div>
        </div>
        {/* Action and Details */}
        <div className="flex items-start gap-2">
           <ActionIcon className={`mt-1 flex-shrink-0 w-4 h-4 ${colorClass}`} />
           <div className="flex-grow min-w-0"> {/* Added min-w-0 for flexbox truncation */}
              <div className={`text-sm font-semibold ${colorClass} truncate`} title={formatAction(log.action)}>
                 {formatAction(log.action)}
                 {affectedUserName && !log.action?.toLowerCase().includes('hours') && (
                   <span className="ml-1 font-normal text-text-light dark:text-text-dark opacity-80">- {affectedUserName}</span>
                 )}
              </div>
               {/* Generic Details Display */}
               {log.details && Object.keys(log.details).length > 0 && (
                 <div className="mt-1.5 ml-1 border-l border-gray-300 dark:border-gray-600 text-xs space-y-1">
                   {groupedDetails().map(([key, value]) => (
                      <div key={key} className="flex">
                         {/* Improved key formatting */}
                         <span className="font-medium capitalize mr-1.5 text-gray-600 dark:text-gray-400 flex-shrink-0">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}:</span>
                         {/* Allow value to wrap/break */}
                         <span className="text-gray-700 dark:text-gray-300 break-words">{formatValue(key, value, log)}</span>
                      </div>
                   ))}
                 </div>
               )}
           </div>
        </div>
      </div>
    );
  };

  if (loading && auditLogs.length === 0) {
    return (
      <div className="p-4">
        <LoadingSkeleton />
      </div>
    );
  }

  if (role !== 'admin') {
    return (
      <div className="min-h-[75vh] flex items-center justify-center bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300 p-4">
        <div className="p-6 sm:p-8 rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark w-full max-w-md">
          <h2 className="text-xl sm:text-2xl font-semibold">Access Denied</h2>
          <p className="mt-4 text-sm sm:text-base">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const handleViewHistory = (traineeId) => {
    setSelectedTraineeId(traineeId);
    setShowModal(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex-1 min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-4 sm:p-6"
    >
      <div className="max-w-3xl mx-auto">
        <motion.h1 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-primary-accent-light dark:text-primary-accent-dark flex items-center"
        >
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <FiSettings className="mr-2" />
          </motion.div>
          Settings
        </motion.h1>

        {/* Settings Sections - Vertical stack */}
        <div className="space-y-6 sm:space-y-8">
          
          {/* Documentation Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark p-4 sm:p-6 rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark"
          >
            <h2 className="text-lg sm:text-xl font-semibold mb-4 text-text-light dark:text-text-dark flex items-center">
              <FiBookOpen className="mr-2" /> Documentation
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Access the internal codebase documentation.
            </p>
            <Link 
              to="/docs"
              className="inline-flex items-center px-4 py-2 bg-accent-light hover:bg-accent-dark dark:bg-accent-dark dark:hover:bg-accent-light text-white dark:text-black rounded-md transition duration-150 ease-in-out"
            >
              View Documentation
            </Link>
          </motion.div>

          {/* User Management Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark p-4 sm:p-6 rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark"
          >
            <div 
              className="flex items-center justify-between cursor-pointer select-none" 
              onClick={() => setIsUserManagementOpen(!isUserManagementOpen)}
            >
              <h2 className="text-lg sm:text-xl font-semibold mb-4 text-text-light dark:text-text-dark flex items-center">
                <FiUser className="mr-2" /> User Management
              </h2>
              <motion.div
                animate={{ rotate: isUserManagementOpen ? 0 : -90 }}
                transition={{ duration: 0.3 }}
              >
                <FiChevronDown size={22} />
              </motion.div>
            </div>
            <AnimatePresence>
              {isUserManagementOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <UserManagement />
                  <div className="mt-4">
                    <InviteUser />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Trainee Assignment Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.0, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark p-4 sm:p-6 rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark"
          >
            <div 
              className="flex items-center justify-between cursor-pointer select-none" 
              onClick={() => setIsTraineeAssignmentsOpen(!isTraineeAssignmentsOpen)}
            >
              <h2 className="text-lg sm:text-xl font-semibold mb-4 text-text-light dark:text-text-dark flex items-center">
                <FiActivity className="mr-2" /> Trainee Assignments
              </h2>
              <motion.div
                animate={{ rotate: isTraineeAssignmentsOpen ? 0 : -90 }}
                transition={{ duration: 0.3 }}
              >
                <FiChevronDown size={22} />
              </motion.div>
            </div>
            <AnimatePresence>
              {isTraineeAssignmentsOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AssignTrainee onViewHistory={handleViewHistory} />
                  <div className="mt-4">
                    <ArchiveTrainees />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Broadcast Notification Section - Component handles its own card and title */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.2, ease: "easeOut" }}
          >
            <BroadcastNotification />
          </motion.div>

          {/* System Settings Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.3, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark p-4 sm:p-6 rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark"
          >
            <h2 className="text-lg sm:text-xl font-semibold mb-4 text-text-light dark:text-text-dark flex items-center">
              <FiDatabase className="mr-2" /> System Settings
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Manage system data integrity and maintenance tasks.
            </p>
            <div className="space-y-3">
              <Link 
                to="/manage-dors"
                className="inline-flex items-center px-4 py-2 bg-accent-light hover:bg-accent-dark dark:bg-accent-dark dark:hover:bg-accent-light text-white dark:text-black rounded-md transition duration-150 ease-in-out w-full sm:w-auto justify-center sm:justify-start"
              >
                <FiFileText className="mr-2 flex-shrink-0" />
                <span className="truncate">Manage DORs</span>
              </Link>
              <Link 
                to="/manage-duplicates"
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white rounded-md transition duration-150 ease-in-out w-full sm:w-auto justify-center sm:justify-start ml-0 sm:ml-3"
              >
                <FiAlertTriangle className="mr-2 flex-shrink-0" />
                <span className="truncate">Manage Duplicate DORs</span>
              </Link>
            </div>
          </motion.div>
          
          {/* Audit Log Section - Placed after other settings cards */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.5, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark p-4 sm:p-6 rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
              <h2 className="text-lg sm:text-xl font-semibold flex items-center">
                <FiActivity className="mr-2" />
                Audit Logs
                <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">
                  (Last {auditLogs.length} entries shown)
                </span>
              </h2>
              
              {/* PDF Download Button - Updated to match DORDetailPage style */}
              {auditLogs.length > 0 && (
                <button 
                  onClick={generateAuditLogsPdf}
                  disabled={generatingPdf}
                  className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow flex items-center gap-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiDownload className="shrink-0" />
                  {generatingPdf ? 'Generating...' : 'PDF'}
                </button>
              )}
            </div>
            
            <div className="space-y-4">
              {auditLogs.map((log, index) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: index * 0.05,
                    ease: "easeOut"
                  }}
                >
                  <AuditLogEntry log={log} />
                </motion.div>
              ))}

              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => fetchAuditLogs(true)}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto"
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      'Loading...'
                    ) : (
                      <>
                        Load More
                        <FiChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                      </>
                    )}
                  </button>
                </div>
              )}

              {auditLogs.length === 0 && !loading && (
                <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400">
                  <p className="text-sm sm:text-base">No audit logs found</p>
                </div>
              )}
            </div>
          </motion.div>
        </div> { /* End of vertical stack */ }

        {/* Modal - This is where AssignmentHistory should render when modal is open */}
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          {selectedTraineeId && <AssignmentHistory traineeId={selectedTraineeId} />} 
        </Modal>
      </div>
    </motion.div>
  );
};

export default SettingsPage;